import { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Table, Dropdown, Badge, Tabs, Tag } from 'antd'
import { useNavigate } from 'react-router';

// Constants
import { TEMPLATE, ROUTES, COMMON_FEATURES } from '../../../../../../constants'
import icon_comment from '../../../../../../assets/svg/comment.svg'

// Components
import { EmptyIndicator } from '../../../../../../components/Indicator'
import { PrimaryButton } from '../../../../../../components/Decoration/Button';
import { Loading, TagLabel, ModalStandard, ModalImage, ButtonLink } from '../../../../../../components'
import DrawerComment from '../VehicleIssuesComment';

// Services
import { TIME, REDUX } from '../../../../../../services/util'

// Redux Actions
import * as REDUX_ACTIONS from '../../../../../..//services/redux/actions'

// Handler
import {
    table_handler,
} from '../../../../../../handlers'

const { getColumnSearchProps, getColumnsFilterTag } = table_handler
const { parseDate } = TIME
const { VEHICLE_ISSUE_STATUS_TITLE, VEHICLE_ISSUE_PRIORITY_TITLE, VEHICLE_ISSUE_STATUS } = TEMPLATE.VAM

const VehicleIssuesPanel = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { uid, name: uname } = useSelector(REDUX.reduxStoreWrapper('user'))
    const { allIDs, staticsByID } = useSelector(REDUX.reduxStoreWrapper('vehicles'))
    const vehicleIssues = useSelector(REDUX.reduxStoreWrapper('vamVehicleIssue.byID'))
    const { itemByID: vamLines } = useSelector(REDUX.reduxStoreWrapper('vamLines'))
    const style = useSelector(REDUX.reduxStoreWrapper('style'))

    const [ dataSource, setDataSource ] = useState([])
    const [ isLoaded, setIsLoaded ] = useState(false);
    const [ isOpenComment, setIsOpenComment ] = useState(false);
    const [ rec, setRec ] = useState({})
    const [ status, setStatus ] = useState('all')
    const [ isOpen, setIsOpen] = useState(false);
    
    const searchTextState = useState('')
    const searchedColumnState = useState('')

    const openDrawer = (data) => {
        setIsOpenComment(true);
        setRec(data);
    }

    const hasFetchedIssues = useRef(false);

    useEffect(() => {
        if (!hasFetchedIssues.current && Object.values(vehicleIssues).length === 0 && allIDs.length > 0) {
            dispatch(REDUX_ACTIONS.v3_vamVehicleIssues.get_vam_vehicle_issue_request(allIDs));
            hasFetchedIssues.current = true;
        }
    }, [vehicleIssues, allIDs, dispatch]);

    const usePrevious = (value) => {
        const ref = useRef();
        useEffect(() => {
          ref.current = value;
        });
        return ref.current;
    }

    const prevVehicles = usePrevious (vehicleIssues);

    const VehicleIssueEditHDLR = (data) => {
        navigate(ROUTES.MODULE_ROUTE[COMMON_FEATURES.FEATURE_NAME.EDIT_VEHICLE_ISSUE], { state : { data }})
    }

    const refreshDataSrc = () => {
        const newData = Object.values(vehicleIssues)
        .map(vh => {
            const vid = vh.vid
            const plate = staticsByID[vid]?.plate || 'Unknown'

            return {
                ...vh,
                key: vh.vamiid,
                plate
            }
        });

        setDataSource(newData)
    }

    useEffect(() => {
        // setIsLoaded(Object.values(vehicleIssues).length > 0 || (Object.values(vehicleIssues).length === 0 && !style.loading.vam));
        setIsLoaded(true)
          if (
              (prevVehicles !== vehicleIssues)
          ) {
              refreshDataSrc()
          }
      },  [vehicleIssues])

    const handleImg = (data) => {
        setIsOpen(true);
        setRec(data);
    }
 
    const columns = [
        {
          title: "Vehicle Plate",
          dataIndex: "plate",
          ...getColumnSearchProps("Vehicle Plate", 'plate', searchTextState, searchedColumnState),
        },
        {
          title: "Status",
          dataIndex: "status",
          render: data => {
            const vhc = VEHICLE_ISSUE_STATUS_TITLE[data]

            return (
                <TagLabel tag={vhc}/>
            )
          },
          filters: getColumnsFilterTag(VEHICLE_ISSUE_STATUS_TITLE),
          onFilter: (value, record) => record.status === value,
        },
        {
          title: "Issue Priority",
          dataIndex: "priorityLevel",
          render: data => {
            const vhc = VEHICLE_ISSUE_PRIORITY_TITLE[data] || { name: 'N/A'}

            return (
                <TagLabel tag={vhc}/>
            ) 
          },
          filters: getColumnsFilterTag(VEHICLE_ISSUE_PRIORITY_TITLE),
          onFilter: (value, record) => record.priorityLevel === value,
        },
        {
            title: "Issue Type",
            dataIndex: "vamlid",
            render: (vamlid) => <Tag key={vamlid}>{vamLines[vamlid]?.name}</Tag>,
            filters: Object.values(vamLines)
                .map(vam => (
                    {
                        text: vam?.name,
                        value: vam?.vamlid,
                    }
                )),
            onFilter: (value, record) => record.vamlid === value,
          },
        {
          title: "Issue Description",
          dataIndex: "desc",
          ...getColumnSearchProps("Issue Description", 'desc', searchTextState, searchedColumnState),
        },
        {
          title: "Attachment",
          render: (record) => {
            const { photos } = record;

            const photoLen = new Set(photos?.map(p => p.url).filter(Boolean)).size

            return photoLen > 0 ? (
                    <ButtonLink onClick={() => handleImg(photos)}>View</ButtonLink>
                ) : (
                    <span style={{ color: 'gray' }}>View</span>
                );
        },
        },
        {
          title: "Issues Comment",
          render: (record) => {
            const { comment_count } = record;

            return (
            <Badge
                count={comment_count || 0}
                showZero
                offset={[10, 10]}
                color="#FA8C16"
            >
                <span 
                    className="ButtonLink"
                    aria-hidden="true"
                    onClick={() => {
                        openDrawer(record);
                        dispatch(REDUX_ACTIONS.v3_vamVehicleIssues.get_comment_vam_vehicle_issue_request(record.vamiid))
                    }}
                >
                    <img src={icon_comment} alt="comment" />
                </span>
            </Badge>
            );
        },
        },
        {
            title: "Reported By",
            sorter: (a, b) => a.reportedAt - b.reportedAt,
            render: data => {

                const name = data.updatedBy === uid ? uname : data.updatedBy

                return (
                    <>
                        <>{name}</><br/>
                        <>{parseDate(Number(data.reportedAt))}</>
                    </>
                )
            }
                
          },
        {
            title: "Created By",
            width: 250,
            defaultSortOrder: "descend",
            sorter: (a, b) => a.createdAt - b.createdAt,
            render: data => {

                const name = data.createdBy === uid ? uname : data.createdBy

                return (
                    <>
                        <>{name}</><br/>
                        <>{parseDate(Number(data.createdAt))}</>
                    </>
                )
            }
          },
        {
            title: "Actions",
            width: 170,
            fixed: 'right',
            render: data => {

                const items = [
                    {
                        key: '1',
                        label: 
                            <span 
                                className="ButtonLink"
                                aria-hidden="true"
                                onClick = {() => VehicleIssueEditHDLR(data)}
                            >
                                Edit
                            </span>
                    },
                    {
                        key: '2',
                        label: data?.status === 'VAMI000' ?
                        <ModalStandard 
                            onClick={() => dispatch(REDUX_ACTIONS.v3_vamVehicleIssues.cu_vam_vehicle_issue_request({ issue: {...data, status: 'VAMI200'}, action: 'U', uid }))}
                            disabled={data?.status !== 'VAMI000'}
                            title={'Mark as closed'}
                            content={'You are about to mark this vehicle issue as closed.'}
                            subcontent={`${data?.plate || '-'}`}
                            // icon={<></>}
                        >
                            Mark as closed
                        </ModalStandard>
                        : <span>Mark as closed</span>
                    },
                    {
                        key: '3',
                        label: data?.status === 'VAMI000' ?
                        <ModalStandard 
                            onClick={() => dispatch(REDUX_ACTIONS.v3_vamVehicleIssues.cu_vam_vehicle_issue_request({ issue: {...data, status: 'VAMI100'}, action: 'U', uid}))}
                            disabled={data?.status !== 'VAMI000'}
                            title={'Mark as resolved'}
                            content={'You are about to mark this vehicle issue as resolved.'}
                            subcontent={`${data?.plate || '-'}`}
                            // icon={<></>}
                        >
                            Mark as resolved
                        </ModalStandard>
                        : <span>Mark as resolved</span>
                    }
                ]
                return <Dropdown menu={{ items }} >Actions</Dropdown> 
            }
          }
      ];

    const { newData } = table_handler.dataByStatus(VEHICLE_ISSUE_STATUS, dataSource, status)
    const filtered = newData[status]

    const TableItems = () => {
      
        const tabkey = [ 'all', 'VAMI000', 'VAMI100', 'VAMI200'  ] 
      
        return tabkey.map(key => {
      
            const name = key === 'all' ? 'All' : VEHICLE_ISSUE_STATUS_TITLE[key].name
      
            return {
                label: `${name}`,
                key,
                children: (
                    dataSource.length > 0
                    ?
                    <Table
                        loading={style.loading.vam}
                        columns={columns && columns.filter(c => !c.isHidden)}
                        dataSource={filtered}
                        pagination={true}
                        scroll={{
                            y: window.innerHeight,
                            x: columns && columns.length * 180
                        }}
                    /> 
                    :
                    <EmptyIndicator />
                ),
            }
        })
      
    };

    const extraBtn =
    <div style={{ display: 'flex', gap: '7px', float: 'right', marginBottom: '15px'}}>
        <PrimaryButton htmlType={'submit'} onClick={() => navigate (ROUTES.MODULE_ROUTE[COMMON_FEATURES.FEATURE_NAME.CREATE_VEHICLE_ISSUE])}>+ Add Vehicle Issues</PrimaryButton>
    </div>

    return (
        <>
            {  !isLoaded ? 
               <Loading /> :
                // dataSource.length > 0 ?
                <Tabs
                    onChange={key => setStatus(key)}
                    items={TableItems()}
                    tabBarExtraContent={extraBtn}
                />
                // :
                //     <EmptyIndicator />
            }
            <DrawerComment isOpen={isOpenComment} setIsOpen={setIsOpenComment} data={rec}/>
            {
                rec.length
                &&
                <ModalImage isOpen={isOpen} setIsOpen={setIsOpen} extra={rec?.filter(r => r.url)} />
            }
        </>
    )
}

export default VehicleIssuesPanel